/**
 * Nominee registry. CRUD for nominees with relation, contact priority,
 * Shamir-holder flag, and encrypted notes.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, NOMINEE_RELATIONS, NOMINEE_DISPLAY } from '../types.ts';
import type { Nominee, NomineeId, NomineeRelation } from '../types.ts';
import { sha256 } from '../chassis/crypto.ts';

const TPL = /* html */ `
<style>
.list-row .badges { display: inline-flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
.list-row.priority-1 { border-left-color: var(--saffron-deep); }
.shamir { color: var(--saffron-deep); font-weight: 700; font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.18em; }
</style>
<section class="frame">
  <h2>Nominees <span class="deva">नामांकित</span></h2>
  <div class="kicker">Who receives what · who holds the keys</div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <div class="row">
      <label class="field">First name + last
        <input id="name" type="text" required placeholder="e.g. Priya Sharma" />
      </label>
      <label class="field">Relation
        <select id="relation" required>
          ${NOMINEE_RELATIONS.map((r) => `<option value="${r}">${NOMINEE_DISPLAY[r].en}</option>`).join('')}
        </select>
      </label>
    </div>
    <div class="row">
      <label class="field">Phone (hashed, never stored raw)
        <input id="phone" type="text" inputmode="numeric" placeholder="+91…" />
      </label>
      <label class="field">Aadhaar last 4
        <input id="aadhaar" type="text" inputmode="numeric" maxlength="4" placeholder="####" />
      </label>
    </div>
    <div class="row">
      <label class="field">DigiLocker ID (optional)
        <input id="digilocker" type="text" placeholder="optional" />
      </label>
      <label class="field">Contact priority
        <select id="priority">
          <option value="1">1 · Primary</option>
          <option value="2">2 · Backup</option>
          <option value="3">3 · Third</option>
          <option value="4">4 · Fourth</option>
          <option value="5">5 · Fifth</option>
        </select>
      </label>
    </div>
    <label class="field">
      <span style="display:flex;align-items:center;gap:8px;">
        <input id="shamir" type="checkbox" style="width:auto;" />
        <span>Holds a Shamir share for the dead-man-switch</span>
      </span>
    </label>
    <label class="field">Notes (encrypted at rest)
      <textarea id="notes" rows="2" placeholder="e.g. spouse, joint home loan, knows locker combination"></textarea>
    </label>
    <div class="btn-row">
      <button type="submit" class="btn">Add nominee</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatNomineeRegistry extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      if (!list) return;
      if (v.nominees.length === 0) {
        list.innerHTML = `<div class="list-empty">No nominees yet. Add the first one below.</div>`;
        return;
      }
      const sorted = [...v.nominees].sort((a, b) => a.contact_priority - b.contact_priority);
      list.innerHTML = sorted.map((n) => this.#row(n)).join('');
      list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
        b.addEventListener('click', () => {
          const id = b.dataset['del'] as NomineeId;
          void store.mutate((d) => {
            d.nominees = d.nominees.filter((x) => x.id !== id);
            for (const a of d.assets) a.nominee_ids = a.nominee_ids.filter((nid) => nid !== id);
            if (d.dead_man_switch.secondary_verifier_id === id) {
              d.dead_man_switch.secondary_verifier_id = null;
            }
          });
        });
      });
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = (this.#shadow.getElementById('name') as HTMLInputElement).value.trim();
      const relation = (this.#shadow.getElementById('relation') as HTMLSelectElement).value as NomineeRelation;
      const phone = (this.#shadow.getElementById('phone') as HTMLInputElement).value.trim();
      const aadhaar = (this.#shadow.getElementById('aadhaar') as HTMLInputElement).value.trim();
      const digilocker = (this.#shadow.getElementById('digilocker') as HTMLInputElement).value.trim();
      const priority = Number((this.#shadow.getElementById('priority') as HTMLSelectElement).value) as 1 | 2 | 3 | 4 | 5;
      const isShamir = (this.#shadow.getElementById('shamir') as HTMLInputElement).checked;
      const notes = (this.#shadow.getElementById('notes') as HTMLTextAreaElement).value.trim();
      if (!name) return;
      const phone_hash = phone ? await sha256(phone) : '';
      const nom: Nominee = {
        id: newId<NomineeId>(),
        relation,
        display_name: name,
        aadhaar_last4: aadhaar.replace(/\D/g, '').slice(-4),
        phone_hash,
        digilocker_id: digilocker,
        contact_priority: priority,
        is_shamir_holder: isShamir,
        notes,
        created_at: new Date().toISOString(),
      };
      await store.mutate((d) => {
        d.nominees.push(nom);
      });
      form.reset();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  #row(n: Nominee): string {
    const rel = NOMINEE_DISPLAY[n.relation];
    const badges: string[] = [];
    badges.push(`<span class="pill indigo">P${n.contact_priority}</span>`);
    if (n.is_shamir_holder) badges.push(`<span class="pill saffron">Shamir</span>`);
    if (n.aadhaar_last4) badges.push(`<span class="pill">A ****${escapeHtml(n.aadhaar_last4)}</span>`);
    if (n.digilocker_id) badges.push(`<span class="pill leaf">DigiLocker</span>`);
    return `<div class="list-row priority-${n.contact_priority}">
      <div>
        <div class="title">${escapeHtml(n.display_name)}</div>
        <div class="sub">${rel.en} · ${rel.hi}${n.notes ? ' · ' + escapeHtml(n.notes.slice(0, 80)) : ''}</div>
        <div class="badges">${badges.join(' ')}</div>
      </div>
      <button class="btn tiny ghost" data-del="${n.id}">Remove</button>
    </div>`;
  }
}

customElements.define('wasiyat-nominee-registry', WasiyatNomineeRegistry);
